// Watermark fill via Telea fast-marching inpaint.
// Pure JS, no canvas/WASM — returns a new ImageData; input is never mutated.

import type { Rect } from '~/types/pipeline';

const KNOWN = 0;
const BAND = 1;
const INSIDE = 2;
const HUGE_T = 1e6;

class MinHeap {
  private a: Array<{ t: number; i: number }> = [];

  get size(): number {
    return this.a.length;
  }

  push(t: number, i: number): void {
    const a = this.a;
    a.push({ t, i });
    let n = a.length - 1;
    while (n > 0) {
      const p = (n - 1) >> 1;
      const parent = a[p];
      const child = a[n];
      if (!parent || !child || parent.t <= child.t) break;
      a[p] = child;
      a[n] = parent;
      n = p;
    }
  }

  pop(): { t: number; i: number } | null {
    const a = this.a;
    if (a.length === 0) return null;
    const top = a[0];
    const last = a.pop();
    if (!top || !last) return null;
    if (a.length > 0) {
      a[0] = last;
      let n = 0;
      const N = a.length;
      while (true) {
        const l = 2 * n + 1;
        const r = 2 * n + 2;
        let s = n;
        const left = a[l];
        const right = a[r];
        const smallest = a[s];
        if (!smallest) break;
        if (l < N && left && left.t < smallest.t) s = l;
        const currentSmallest = a[s];
        if (!currentSmallest) break;
        if (r < N && right && right.t < currentSmallest.t) s = r;
        if (s === n) break;
        const swapTarget = a[s];
        const current = a[n];
        if (!swapTarget || !current) break;
        a[s] = current;
        a[n] = swapTarget;
        n = s;
      }
    }
    return top;
  }
}

function solveEikonal(t1: number, f1: number, t2: number, f2: number): number {
  if (f1 !== INSIDE && f2 !== INSIDE) {
    const r = 2 - (t1 - t2) * (t1 - t2);
    if (r > 0) {
      const s = Math.sqrt(r);
      let t = (t1 + t2 - s) * 0.5;
      if (t >= t1 && t >= t2) return t;
      t = (t1 + t2 + s) * 0.5;
      if (t >= t1 && t >= t2) return t;
    }
    return Math.min(t1, t2) + 1;
  }
  if (f1 !== INSIDE) return t1 + 1;
  if (f2 !== INSIDE) return t2 + 1;
  return HUGE_T;
}

function gradAt(
  C: Float32Array,
  flag: Uint8Array,
  idx: number,
  x: number,
  y: number,
  cw: number,
  ch: number,
): { gx: number; gy: number } {
  let gx = 0;
  let gy = 0;
  const left = x > 0 && flag[idx - 1] === KNOWN;
  const right = x < cw - 1 && flag[idx + 1] === KNOWN;
  const up = y > 0 && flag[idx - cw] === KNOWN;
  const down = y < ch - 1 && flag[idx + cw] === KNOWN;
  if (left && right) gx = ((C[idx + 1] ?? 0) - (C[idx - 1] ?? 0)) * 0.5;
  else if (right) gx = (C[idx + 1] ?? 0) - (C[idx] ?? 0);
  else if (left) gx = (C[idx] ?? 0) - (C[idx - 1] ?? 0);
  if (up && down) gy = ((C[idx + cw] ?? 0) - (C[idx - cw] ?? 0)) * 0.5;
  else if (down) gy = (C[idx + cw] ?? 0) - (C[idx] ?? 0);
  else if (up) gy = (C[idx] ?? 0) - (C[idx - cw] ?? 0);
  return { gx, gy };
}

// In-place Telea inpaint on imageData. Direct port of src/inpaint-telea.js.
function inpaintTelea(imageData: ImageData, rect: Rect, radius = 3): void {
  if (rect.w <= 0 || rect.h <= 0) return;
  const W = imageData.width;
  const H = imageData.height;
  const pad = radius + 2;

  const cx0 = Math.max(0, rect.x - pad);
  const cy0 = Math.max(0, rect.y - pad);
  const cx1 = Math.min(W, rect.x + rect.w + pad);
  const cy1 = Math.min(H, rect.y + rect.h + pad);
  const cw = cx1 - cx0;
  const ch = cy1 - cy0;
  if (cw <= 0 || ch <= 0) return;

  const N = cw * ch;
  const flag = new Uint8Array(N);
  const T = new Float32Array(N);
  const R = new Float32Array(N);
  const G = new Float32Array(N);
  const B = new Float32Array(N);

  const src = imageData.data;
  for (let y = 0; y < ch; y++) {
    for (let x = 0; x < cw; x++) {
      const idx = y * cw + x;
      const sIdx = ((cy0 + y) * W + (cx0 + x)) * 4;
      R[idx] = src[sIdx] ?? 0;
      G[idx] = src[sIdx + 1] ?? 0;
      B[idx] = src[sIdx + 2] ?? 0;
    }
  }

  const mx0 = rect.x - cx0;
  const my0 = rect.y - cy0;
  const mx1 = rect.x + rect.w - cx0;
  const my1 = rect.y + rect.h - cy0;
  for (let y = 0; y < ch; y++) {
    const inMy = y >= my0 && y < my1;
    for (let x = 0; x < cw; x++) {
      const idx = y * cw + x;
      if (inMy && x >= mx0 && x < mx1) {
        flag[idx] = INSIDE;
        T[idx] = HUGE_T;
      } else {
        flag[idx] = KNOWN;
        T[idx] = 0;
      }
    }
  }

  const heap = new MinHeap();
  for (let y = my0; y < my1; y++) {
    for (let x = mx0; x < mx1; x++) {
      const idx = y * cw + x;
      if (flag[idx] !== INSIDE) continue;
      const adjacent =
        (x > 0 && flag[idx - 1] === KNOWN) ||
        (x < cw - 1 && flag[idx + 1] === KNOWN) ||
        (y > 0 && flag[idx - cw] === KNOWN) ||
        (y < ch - 1 && flag[idx + cw] === KNOWN);
      if (adjacent) {
        flag[idx] = BAND;
        T[idx] = 0;
        heap.push(0, idx);
      }
    }
  }

  const r2 = radius * radius;

  while (heap.size > 0) {
    const top = heap.pop();
    if (!top) break;
    const idx = top.i;
    if (flag[idx] === KNOWN) continue;
    const x = idx % cw;
    const y = (idx - x) / cw;
    const tp = T[idx] ?? 0;

    let sw = 0;
    let sr = 0;
    let sg = 0;
    let sb = 0;
    const x0 = Math.max(0, x - radius);
    const x1 = Math.min(cw - 1, x + radius);
    const y0 = Math.max(0, y - radius);
    const y1 = Math.min(ch - 1, y + radius);
    for (let yy = y0; yy <= y1; yy++) {
      const dy = yy - y;
      const dy2 = dy * dy;
      for (let xx = x0; xx <= x1; xx++) {
        const dx = xx - x;
        const d2 = dx * dx + dy2;
        if (d2 === 0 || d2 > r2) continue;
        const qIdx = yy * cw + xx;
        if (flag[qIdx] !== KNOWN) continue;
        const dist = Math.sqrt(d2);
        const lev = 1 / (1 + Math.abs((T[qIdx] ?? 0) - tp));
        const w = lev / (d2 * dist);
        const gR = gradAt(R, flag, qIdx, xx, yy, cw, ch);
        const gG = gradAt(G, flag, qIdx, xx, yy, cw, ch);
        const gB = gradAt(B, flag, qIdx, xx, yy, cw, ch);
        sw += w;
        sr += w * ((R[qIdx] ?? 0) + gR.gx * dx + gR.gy * dy);
        sg += w * ((G[qIdx] ?? 0) + gG.gx * dx + gG.gy * dy);
        sb += w * ((B[qIdx] ?? 0) + gB.gx * dx + gB.gy * dy);
      }
    }
    if (sw > 0) {
      R[idx] = Math.max(0, Math.min(255, sr / sw));
      G[idx] = Math.max(0, Math.min(255, sg / sw));
      B[idx] = Math.max(0, Math.min(255, sb / sw));
    }
    flag[idx] = KNOWN;

    const neigh: Array<[number, number, number]> = [
      [x - 1, y, idx - 1],
      [x + 1, y, idx + 1],
      [x, y - 1, idx - cw],
      [x, y + 1, idx + cw],
    ];
    for (const [nx, ny, nIdx] of neigh) {
      if (nx === undefined || ny === undefined || nIdx === undefined) continue;
      if (nx < 0 || nx >= cw || ny < 0 || ny >= ch) continue;
      if (flag[nIdx] === KNOWN) continue;
      const tL = nx > 0 ? (T[nIdx - 1] ?? HUGE_T) : HUGE_T;
      const fL = nx > 0 ? (flag[nIdx - 1] ?? INSIDE) : INSIDE;
      const tR = nx < cw - 1 ? (T[nIdx + 1] ?? HUGE_T) : HUGE_T;
      const fR = nx < cw - 1 ? (flag[nIdx + 1] ?? INSIDE) : INSIDE;
      const tU = ny > 0 ? (T[nIdx - cw] ?? HUGE_T) : HUGE_T;
      const fU = ny > 0 ? (flag[nIdx - cw] ?? INSIDE) : INSIDE;
      const tD = ny < ch - 1 ? (T[nIdx + cw] ?? HUGE_T) : HUGE_T;
      const fD = ny < ch - 1 ? (flag[nIdx + cw] ?? INSIDE) : INSIDE;

      let tx: number;
      let fx: number;
      if (fL !== INSIDE && (fR === INSIDE || tL <= tR)) {
        tx = tL;
        fx = fL;
      } else if (fR !== INSIDE) {
        tx = tR;
        fx = fR;
      } else {
        tx = HUGE_T;
        fx = INSIDE;
      }
      let ty: number;
      let fy: number;
      if (fU !== INSIDE && (fD === INSIDE || tU <= tD)) {
        ty = tU;
        fy = fU;
      } else if (fD !== INSIDE) {
        ty = tD;
        fy = fD;
      } else {
        ty = HUGE_T;
        fy = INSIDE;
      }
      const tNew = solveEikonal(tx, fx, ty, fy);
      if (tNew < (T[nIdx] ?? HUGE_T)) {
        T[nIdx] = tNew;
        flag[nIdx] = BAND;
        heap.push(tNew, nIdx);
      }
    }
  }

  // Write inpainted RGB back; preserve original alpha (matches JS ref).
  for (let y = my0; y < my1; y++) {
    for (let x = mx0; x < mx1; x++) {
      const lIdx = y * cw + x;
      const sIdx = ((cy0 + y) * W + (cx0 + x)) * 4;
      src[sIdx] = R[lIdx] ?? 0;
      src[sIdx + 1] = G[lIdx] ?? 0;
      src[sIdx + 2] = B[lIdx] ?? 0;
      // src[sIdx + 3] intentionally unchanged.
    }
  }
}

/**
 * Apply Telea fast-marching inpaint to fill the given region.
 * Returns a new ImageData; input is not mutated.
 */
export function applyClassicalFill(image: ImageData, region: Rect): ImageData {
  const copy = new ImageData(new Uint8ClampedArray(image.data), image.width, image.height);
  inpaintTelea(copy, region);
  return copy;
}
